import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-query-editor>` says about itself.
 *
 * The thing to know before reaching for it is what it does NOT do: it does not run anything. There
 * is no transport in a component library, and a piece that both composed a request and sent it
 * would be unusable by anybody whose queries go somewhere it had not heard of. Pressing Run raises
 * an event; the application runs it and sets `running` while it does.
 *
 * The second thing is the engine. Nothing here imports an editor. The editing surface is mounted
 * through `code-surface-engine.ts`, so the library ships with a working plain-text editor and an
 * application that wants highlighting and completion installs one — with no change to this
 * component or to any page using it.
 */
const docs: ComponentDocs = {
  aliases: ['soql editor', 'query builder', 'query console', 'query tab', 'sql editor'],

  answers: [
    'let somebody write and run a query',
    'keep several queries open at once',
    'show which open queries have unsaved changes',
    'give a developer an editor without choosing an editor library for them',
  ],

  placements: ['appPage'],

  whenToUse:
    'On a query surface, where somebody writes a query, runs it, and comes back to it — and where ' +
    'keeping the previous attempt matters as much as running the next one.',

  insteadUse: [
    {
      name: 'CodeEditor',
      when:
        'the text is a stored artifact being edited rather than a question being asked — the code ' +
        'editor is built around fetching, dirty state and saving back, which a query has no notion of.',
    },
    {
      name: 'TextField',
      when: 'it is one short expression on a form, not a body of text somebody works in.',
    },
  ],

  inputs: [
    {
      name: 'tabs',
      type: 'json',
      description:
        'The open queries: `{ key, label, query }[]`. Re-supplying the same list does NOT overwrite ' +
        'what somebody has typed since — a host that re-renders on every keystroke would otherwise ' +
        'erase the thing being typed. A tab dropped from the list has its text forgotten with it.',
      default: [],
    },
    {
      name: 'active',
      attribute: 'active',
      type: 'text',
      property: true,
      description:
        'Which tab is on screen, by key. An unknown key falls back to the first tab rather than ' +
        'showing an empty editor.',
      readsBack: 'The key actually being shown, which is the fallback when the attribute names no open tab.',
    },
    {
      name: 'language',
      attribute: 'language',
      type: 'text',
      description:
        'What kind of text this is, passed to the engine as a hint. Changing it remounts the engine, ' +
        'carrying the current text across. Engines that do not know the language show plain text.',
      default: 'sql',
      fallback: 'plain text, for any language the mounted engine does not recognise',
    },
    {
      name: 'running',
      attribute: 'running',
      type: 'boolean',
      description:
        'A run is in flight. The Run button reads "Running…" and is disabled, and Ctrl/Cmd+Enter ' +
        'raises nothing — so a person leaning on the shortcut cannot queue six copies of one query.',
      default: false,
    },
    {
      name: 'status',
      attribute: 'status',
      type: 'text',
      description: 'A line under the editor — what the last run returned, or what went wrong with it.',
    },
    {
      name: 'runLabel',
      attribute: 'run-label',
      type: 'text',
      description: 'What the run button is called, for a surface whose verb is not "Run".',
      default: 'Run',
    },
    {
      name: 'searchTerm',
      attribute: 'search-term',
      type: 'text',
      description: 'What the in-editor Search control looks for when it is pressed.',
    },
  ],

  events: [
    {
      name: 'caos-query-run',
      when: 'The run button is pressed, or Ctrl/Cmd+Enter is used in the editor. Never while `running`.',
      carries: '`{ key, query }` — which tab, and its text as it stands.',
    },
    {
      name: 'caos-query-change',
      when: 'The person edits the query.',
      carries: '`{ key, query }`.',
    },
    {
      name: 'caos-query-tab',
      when: 'A different tab is selected.',
      carries: '`{ key }`.',
    },
    {
      name: 'caos-query-prettify',
      when: 'The Prettify control is pressed.',
      carries:
        '`{ key, query, language }`. Formatting is language knowledge this component does not have; ' +
        'the application formats and calls `setQuery` back.',
    },
    {
      name: 'caos-query-copy',
      when: 'The Copy control is pressed.',
      carries:
        '`{ text }`. It asks rather than reaching for the clipboard, because a component cannot know ' +
        'whether this page is permitted to write to it and a silent rejection is worse than an event.',
    },
    {
      name: 'caos-query-search',
      when: 'The Search control is pressed, or `search()` is called.',
      carries: '`{ term, matches }` — how many times it occurs, as the mounted engine counts them.',
    },
  ],

  state: [
    {
      name: 'value',
      type: 'string',
      description: 'What is in the editor right now, for the tab on screen.',
    },
    {
      name: 'changed',
      type: 'string[]',
      description:
        'The keys of tabs whose text differs from what was handed in. A comparison, not a flag ' +
        'somebody has to remember to set, so it cannot go stale.',
    },
    {
      name: 'engine',
      type: 'string',
      description:
        'The name of the mounted editing engine — `plain-text` unless an application installed ' +
        'another. Readable so a surface can say which editor it is drawing.',
    },
  ],

  methods: [
    {
      name: 'setQuery',
      description:
        'Put text into a tab from outside — what a host calls after prettifying, or after loading a ' +
        'saved query. Does nothing for a key that is not open.',
      args: [
        { name: 'key', type: 'string', description: 'Which tab.' },
        { name: 'query', type: 'string', description: 'The text to put in it.' },
      ],
    },
    {
      name: 'search',
      description:
        'Find a term in the query and reveal it, returning how many times it occurs. Returns 0 when ' +
        'the mounted engine cannot search — the same answer as "not found", which is why the toolbar ' +
        'control is disabled in that case rather than left to give a misleading zero.',
      args: [{ name: 'term', type: 'string', description: 'What to look for.' }],
    },
  ],

  parts: [
    { part: 'editor', is: 'The outer column.' },
    { part: 'tabs', is: 'The tab strip.' },
    { part: 'tab', is: 'One open query’s tab.' },
    { part: 'toolbar', is: 'The search / copy / prettify cluster on the editor’s right edge.' },
    { part: 'tool', is: 'One of those three controls.' },
    { part: 'run', is: 'The run button.' },
    { part: 'status', is: 'The line under the editor.' },
    {
      part: 'editor-surface',
      is: 'The editing surface itself.',
      when: 'The built-in plain-text engine is mounted; another engine names its own parts, or none.',
    },
  ],

  accessibility:
    'The tab strip is a real `role="tablist"` of buttons, with `aria-selected` and roving tabindex, ' +
    'so a keyboard reaches the tabs before the editor rather than having to pass through it. Unsaved ' +
    'work is marked with a dot AND an `aria-label` reading "unsaved changes", because a bullet is ' +
    'not a word. The status line is a `role="status"` live region, so what a run returned is ' +
    'announced to somebody who was not watching the button. The editing surface carries its own ' +
    'accessible name, which every engine is required to apply — an unlabelled editing region is the ' +
    'most common way a surface like this becomes unusable without sight.',

  examples: [
    {
      key: 'several-open',
      label: 'Three queries open, one edited',
      summary:
        'The ordinary working state: the tab being worked in, two kept from earlier, and a dot on ' +
        'the one whose text no longer matches what was handed in.',
      placement: {
        component: 'caos-query-editor',
        inputs: {
          active: 'accounts',
          status: '42 rows · ran in 310 ms',
          tabs: [
            { key: 'accounts', label: 'Accounts', query: 'select id, name, industry from account\nwhere industry = :industry' },
            { key: 'contacts', label: 'Contacts', query: 'select id, email from contact limit 200' },
            { key: 'scratch', label: 'Scratch', query: '' },
          ],
        },
      },
      stage: { needsWidth: 680 },
    },
    {
      key: 'running',
      label: 'A run in flight',
      summary:
        'While `running`, the button says so and is disabled, and the keyboard shortcut raises ' +
        'nothing — leaning on Ctrl+Enter cannot queue six copies of one query.',
      placement: {
        component: 'caos-query-editor',
        inputs: {
          running: true,
          status: 'Running…',
          tabs: [{ key: 'accounts', label: 'Accounts', query: 'select count() from account' }],
        },
      },
      stage: { needsWidth: 680 },
    },
    {
      key: 'empty',
      label: 'Nothing open',
      summary:
        'With no tabs there is nothing to run, and the run button says so by being disabled rather ' +
        'than by raising an event with no query in it.',
      placement: { component: 'caos-query-editor', inputs: { tabs: [] } },
      stage: { needsWidth: 680 },
    },
  ],
};

export default docs;
